import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, EmptyState, Tooltip } from '../../components';
import {
  getMyStartup, updateMyStartup, uploadStartupDocument, deleteStartupDocument,
  StartupDoc, StartupDocument,
} from '../../services/startup-service';

interface CoFounderEntry {
  name: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'declined';
  isFocalTrainee: boolean;
}

const INDUSTRIES = ['Tech', 'Healthcare', 'Education', 'Finance', 'Other'];
const STAGES = [
  { value: 'idea',     label: 'Idea' },
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed',     label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
];

const fmtSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MyStartup = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [startup,      setStartup]      = useState<StartupDoc | null>(null);
  const [isLoading,    setIsLoading]    = useState(true);
  const [isSaving,     setIsSaving]     = useState(false);
  const [saveSuccess,  setSaveSuccess]  = useState(false);
  const [saveError,    setSaveError]    = useState('');
  const [uploadTitle,  setUploadTitle]  = useState('');
  const [isUploading,  setIsUploading]  = useState(false);
  const [deleteConfirm,  setDeleteConfirm]  = useState<string | null>(null);
  const [isDeleting,     setIsDeleting]     = useState(false);
  const [form, setForm] = useState({
    name: '', tagline: '', description: '', industry: 'Tech', stage: 'idea',
    website: '', teamSize: 1, foundingYear: new Date().getFullYear(),
    problemStatement: '', solution: '', targetMarket: '', revenueModel: '', traction: '',
  });
  const [coFounders, setCoFounders] = useState<CoFounderEntry[]>([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'CTO' });

  useEffect(() => {
    getMyStartup()
      .then(s => {
        setStartup(s);
        if (s) populateForm(s);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const populateForm = (s: StartupDoc) => {
    setForm({
      name:             s.name || '',
      tagline:          s.tagline || '',
      description:      s.description || '',
      industry:         s.industry || 'Tech',
      stage:            s.stage || 'idea',
      website:          s.website || '',
      teamSize:         s.teamSize || 1,
      foundingYear:     s.foundingYear || new Date().getFullYear(),
      problemStatement: s.problemStatement || '',
      solution:         s.solution || '',
      targetMarket:     s.targetMarket || '',
      revenueModel:     s.revenueModel || '',
      traction:         s.traction || '',
    });
    // Populate co-founders from startup data if available
    if ((s as any).coFounders) {
      setCoFounders((s as any).coFounders);
    }
  };

  const handleInviteCoFounder = () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) return;
    setCoFounders(prev => [...prev, {
      name: inviteForm.name,
      email: inviteForm.email,
      role: inviteForm.role,
      status: 'pending',
      isFocalTrainee: false,
    }]);
    setInviteForm({ name: '', email: '', role: 'CTO' });
    setShowInviteForm(false);
  };

  const set = (field: string, value: any) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const updated = await updateMyStartup({ ...form, coFounders });
      setStartup(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const titleForLog = uploadTitle || file.name;
      const updated = await uploadStartupDocument(file, titleForLog);
      setStartup(updated);
      setUploadTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setSaveError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (url: string, fileName: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const handleDeleteDocument = async (docId: string) => {
    setIsDeleting(true);
    try {
      const updated = await deleteStartupDocument(docId);
      setStartup(updated);
    } catch (err: any) {
      setSaveError(err.message);
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };

  if (isLoading) {
    return <div className="section-gap"><p className="text-muted">Loading...</p></div>;
  }

  if (!startup) {
    return (
      <div className="section-gap">
        <EmptyState
          title="No startup yet"
          description="Apply to the program to create your startup profile."
          action={
            <Button variant="primary" onClick={() => navigate('/app/startup/apply')}>
              Apply to Program
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="section-gap">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text">Business Profile</h1>
          <p className="text-muted mt-1">Manage your startup profile and documents</p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip content="Industry sector — update in the form below" position="bottom">
            <span className="text-xs bg-chart/20 text-chart px-2 py-1 rounded capitalize cursor-default">{startup.industry}</span>
          </Tooltip>
          <Tooltip content="Current startup stage — update in the form below" position="bottom">
            <span className="text-xs bg-surface/50 text-muted px-2 py-1 rounded capitalize cursor-default">{startup.stage}</span>
          </Tooltip>
          <Tooltip content="See every change, milestone, and metric movement over time" position="bottom">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/app/startup/journey')}
            >
              <span className="inline-flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 17l6-6 4 4 8-8M14 7h7v7" />
                </svg>
                View Journey
              </span>
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Profile Section */}
      <div className="border-b border-line pb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-text">{startup.name || 'My Startup'}</h2>
            {startup.tagline && <p className="text-muted text-sm mt-0.5">{startup.tagline}</p>}
          </div>
        </div>

        {saveSuccess && (
          <div className="bg-success/10 border border-success/30 text-success text-sm rounded-lg px-4 py-3 mb-4">
            Changes saved successfully.
          </div>
        )}
        {saveError && (
          <div className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-4 py-3 mb-4">
            {saveError}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text mb-2">Startup Name</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Tagline</label>
              <input value={form.tagline} onChange={e => set('tagline', e.target.value)}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Industry</label>
              <select value={form.industry} onChange={e => set('industry', e.target.value)}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none">
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Stage</label>
              <select value={form.stage} onChange={e => set('stage', e.target.value)}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none">
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Website</label>
              <input value={form.website} onChange={e => set('website', e.target.value)} type="url"
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Team Size</label>
              <input value={form.teamSize} onChange={e => set('teamSize', parseInt(e.target.value, 10) || 1)} type="number" min={1}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Founding Year</label>
              <input value={form.foundingYear} onChange={e => set('foundingYear', parseInt(e.target.value, 10) || new Date().getFullYear())}
                type="number" min={2000} max={new Date().getFullYear()}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none" />
            </div>
          </div>

          {[
            { field: 'description',      label: 'Description' },
            { field: 'problemStatement', label: 'Problem Statement' },
            { field: 'solution',         label: 'Solution' },
            { field: 'targetMarket',     label: 'Target Market' },
            { field: 'revenueModel',     label: 'Revenue Model' },
            { field: 'traction',         label: 'Traction' },
          ].map(({ field, label }) => (
            <div key={field}>
              <label className="block text-sm font-medium text-text mb-2">{label}</label>
              <textarea value={(form as any)[field]} onChange={e => set(field, e.target.value)} rows={3}
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none resize-none focus:border-chart/50" />
            </div>
          ))}

          <Tooltip content="Save all profile changes">
            <Button type="submit" variant="primary" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </Tooltip>
        </form>
      </div>

      {/* Co-Founders Section */}
      <div className="border-b border-line pb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-text">Co-Founders</h3>
            <p className="text-muted text-sm mt-1">Manage your startup team</p>
          </div>
          <Tooltip content="Send an invite to add a team member">
            <Button variant="ghost" size="sm" onClick={() => setShowInviteForm(!showInviteForm)}>
              {showInviteForm ? 'Cancel' : '+ Invite Co-Founder'}
            </Button>
          </Tooltip>
        </div>

        {/* Invite Form — at the top */}
        {showInviteForm && (
          <div className="mb-4 p-4 bg-surface rounded-lg border border-line">
            <h4 className="text-sm font-medium text-text mb-3">Invite a Co-Founder</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-muted mb-1">Name</label>
                <input
                  value={inviteForm.name}
                  onChange={e => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
                />
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Email</label>
                <input
                  value={inviteForm.email}
                  onChange={e => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  type="email"
                  className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
                />
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={e => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-text text-sm focus:outline-none"
                >
                  {['CTO', 'COO', 'CFO', 'CMO', 'CPO', 'Other'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <Button variant="primary" size="sm" onClick={handleInviteCoFounder}>
              Send Invite
            </Button>
          </div>
        )}

        {/* Self row */}
        <div className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-line mb-3">
          <div className="w-8 h-8 bg-chart rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
            Y
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text">You (Founder)</span>
              {coFounders.every(cf => !cf.isFocalTrainee) && (
                <Tooltip content="Primary account holder and program participant">
                  <span className="text-xs bg-accent-soft text-chart px-2 py-0.5 rounded cursor-default">Focal Trainee</span>
                </Tooltip>
              )}
            </div>
          </div>
          <Tooltip content="You are an active member of this startup">
            <span className="text-xs bg-success/10 text-success px-2 py-1 rounded cursor-default">Active</span>
          </Tooltip>
        </div>

        {/* Co-founder list */}
        {coFounders.map((cf, index) => (
          <div key={index} className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-line mb-3">
            <div className="w-8 h-8 bg-muted/20 rounded-full flex items-center justify-center text-muted text-sm font-medium flex-shrink-0">
              {cf.name.charAt(0).toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-text truncate">{cf.name}</span>
                <span className="text-xs text-muted">{cf.role}</span>
                {cf.isFocalTrainee && (
                  <Tooltip content="Primary account holder and program participant">
                    <span className="text-xs bg-accent-soft text-chart px-2 py-0.5 rounded cursor-default">Focal Trainee</span>
                  </Tooltip>
                )}
              </div>
              <p className="text-xs text-muted truncate">{cf.email}</p>
            </div>
            <Tooltip content={
              cf.status === 'accepted' ? 'Co-founder has joined your team' :
              cf.status === 'declined' ? 'Invite was declined' :
              'Invite sent, awaiting response'
            }>
              <span className={`text-xs px-2 py-1 rounded cursor-default ${
                cf.status === 'accepted' ? 'bg-success/10 text-success' :
                cf.status === 'declined' ? 'bg-danger/10 text-danger' :
                'bg-warning/10 text-warning'
              }`}>
                {cf.status.charAt(0).toUpperCase() + cf.status.slice(1)}
              </span>
            </Tooltip>
          </div>
        ))}

        {coFounders.length === 0 && (
          <p className="text-muted text-sm">No co-founders added yet.</p>
        )}
      </div>

      {/* Documents Section */}
      <div className="pt-2">
        <h3 className="text-xl font-semibold text-text mb-2">Documents</h3>
        <p className="text-sm text-muted mb-4 max-w-2xl leading-relaxed">
          Upload anything that tells your story: pitch decks, design documents, client contracts,
          vision statements, financial reports, projections, and market analyses. Files appear here
          and on your public profile in the order you add them.
        </p>

        {(startup.documents || []).length === 0 ? (
          <p className="text-muted text-sm mb-4">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-2 mb-6">
            {startup.documents.map((doc: StartupDocument) => (
              <div key={doc._id} className="flex items-center justify-between p-3 bg-surface/50 rounded-lg border border-line">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <svg className="w-8 h-8 text-muted flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                  <div className="min-w-0">
                    <div className="text-text text-sm font-medium truncate">{doc.title || doc.fileName}</div>
                    <div className="text-muted text-xs">
                      {doc.fileName} · {fmtSize(doc.fileSize)} · {new Date(doc.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                  <Tooltip content="Download file">
                    <button onClick={() => handleDownload(doc.fileUrl, doc.fileName)} className="p-2 rounded-lg text-muted hover:text-text hover:bg-surface transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    </button>
                  </Tooltip>
                  <Tooltip content="Open in new tab">
                    <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg text-muted hover:text-text hover:bg-surface transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </a>
                  </Tooltip>
                  {deleteConfirm === doc._id ? (
                    <div className="flex items-center gap-1">
                      <Tooltip content="Confirm delete">
                        <button onClick={() => handleDeleteDocument(doc._id)} disabled={isDeleting} className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors disabled:opacity-50">
                          {isDeleting
                            ? <div className="w-4 h-4 border-2 border-danger border-t-transparent rounded-full animate-spin" />
                            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          }
                        </button>
                      </Tooltip>
                      <Tooltip content="Cancel">
                        <button onClick={() => setDeleteConfirm(null)} className="p-2 rounded-lg text-muted hover:text-text hover:bg-surface transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </Tooltip>
                    </div>
                  ) : (
                    <Tooltip content="Delete file permanently">
                      <button onClick={() => setDeleteConfirm(doc._id)} className="p-2 rounded-lg text-muted hover:text-danger hover:bg-danger/10 transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload */}
        <div className="border-t border-line pt-4">
          <h4 className="text-sm font-medium text-text mb-3">Upload Document</h4>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs text-muted mb-1">Title (optional)</label>
              <input
                value={uploadTitle}
                onChange={e => setUploadTitle(e.target.value)}
                placeholder="e.g. Pitch Deck"
                className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
              />
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUpload}
                className="hidden"
                id="doc-upload"
                disabled={isUploading}
              />
              <Tooltip content="Upload a PDF, DOC, or image file">
                <label htmlFor="doc-upload">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isUploading ? 'Uploading…' : 'Upload File'}
                  </Button>
                </label>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyStartup;
